/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";

import backdropImage from "images/home/tile.svg";
import { HomeColumn } from "components/shared/Splash";
import styled from "styled-components";
import { devices, fontFamilies } from "components/shared/Styles";
import { getUserLocale } from "utilities/UserSettingsUtilities";
import { AppPath } from "components/AppPath";
import { LandingPageStudioHeroImages } from "components/shared/LocalizedImages";
import { LocaleLink } from "utilities/NavigationUtilities";

export const StudioHero: React.FC = () => {
  const locale: string = getUserLocale()!;
  const mainLanguageCode = locale.split("-")[0];
  const localeImage =
    LandingPageStudioHeroImages[mainLanguageCode] ??
    LandingPageStudioHeroImages.en;
  return (
    <StudioHeroBackdrop>
      <HomeColumn>
        <StudioHeroText>
          <StudioHeroTitle>{I18n.t("home.studio_hero.title")}</StudioHeroTitle>
          <StudioHeroSubtitle>
            {I18n.t("home.studio_hero.subtitle")}
          </StudioHeroSubtitle>
        </StudioHeroText>
        <LocaleLink to={AppPath.STUDIO} requireAuthentication={true}>
          <StudioHeroImg srcSet={localeImage.srcSet} />
        </LocaleLink>
      </HomeColumn>
    </StudioHeroBackdrop>
  );
};

const StudioHeroText = styled.div`
  text-align: center;
  margin: auto auto 84px;
  max-width: 907px;
`;

const StudioHeroImg = styled.img`
  width: 100%;
`;

const StudioHeroTitle = styled.h2`
  font: normal normal 900 48px/56px ${fontFamilies};
  color: var(--spectrum-global-color-static-black);
  margin-bottom: 16px;

  ${devices.small} {
    font: normal normal 900 34px/40px ${fontFamilies};
  }
`;

const StudioHeroSubtitle = styled.h3`
  font: normal normal 700 24px/30.05px ${fontFamilies};
  color: var(--spectrum-global-color-static-black);
`;

const StudioHeroBackdrop = styled.div`
  background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 100) 12.89%,
      rgba(255, 255, 255, 0) 100%
    ),
    url("${backdropImage}"),
    linear-gradient(180deg, #ffffff 12.89%, #eff0ff 100%);
  padding-top: 92px;
  padding-bottom: 96px;

  ${devices.small} {
    padding: 48px 24px;
  }
`;
